import {DrawingMode} from "./map/drawingTypes";

export enum SpotType {
    Marker = "Marker",
    Circle = "Circle",
    Polygon = "Polygon"
}

export const overlayTypeToSpotType = (overlayType: DrawingMode) => {
    switch (overlayType) {
        case DrawingMode.CIRCLE:
            return SpotType.Circle

        case DrawingMode.POLYGON:
            return SpotType.Polygon

        default:
            return SpotType.Marker
    }
}

export enum AreaZoneIds {
    Boundaries = "boundaries",
    Entry = "entry",
    Exit = "exit",
    Maintenance = "maintenance"
}

export type AreaZones = {
    boundaries?: BoundariesZone,
    entry?: Zone,
    exit?: ExitZone,
    maintenance?: MaintenanceZone
    ambientZones?: Zone[]
}

export const AreaZoneTemplate: Zone = {
    id: "",
    name: "",
    playMode: "play",
    beaconSensitivity: 8,
    stopOtherAmbients: false,
    loop: false,
    runOnes: false,
    playFullAfter: 0,
    soundFiles: {
        ambient: undefined
    }
}

export type Area = {
    id: string,
    name: string,
    organizationId?: string,
    public?: boolean,
    publicAccessCode?: string,
    summary?: string,
    description?: string,
    coverImageUrl?: string,
    creator?: string,
    position?: LatLong,
    altSyncServerAddress?: string,
    zones: AreaZones,
    modifiedAt: number,
    key: string,
    status?: string,
    hasUnpublishedChanges?: boolean,
    languages?: AreaLanguages,
    showMap?: boolean,
    developerMode?: boolean,
    showSoundInfo?: AreaShowSoundInfoMode,
    showTransport?: boolean,
    defaultVolume?: number,
    setDefaultVolumeOnReset?: boolean,
    appOverridesSystemVolume?: boolean,
    disableVolumeButtons?: boolean,
    bleScanMode?: BleScanMode,
    spotTriggerMode?: AreaSpotTriggerMode,
    autoPlay?: boolean,
    discoverMode?: AreaDiscoverMode,
    defaults?: AreaDefaults,
    groupMode?: AreaGroupMode[],
    groupModeAutoWindow?: number,
    useBoundariesZone?: boolean,
    iosCompatible?: boolean,
    audioMode?: AudioMode,
    flowMode?: AreaFlowMode,
    downloadFrom?: string,
    downloadTo?: string,
    playFrom?: string,
    playTo?: string
}

export const BLE_SCAN_MODE_DONGLE_OR_PHONE = "dongleOrPhone";
export const BLE_SCAN_MODE_DONGLE_ONLY = "dongleOnly";
export const BLE_SCAN_MODE_NONE = "none";
export type BleScanMode = "dongleOrPhone" | "dongleOnly" | "none";

export const AREA_GROUP_MODE_FIXED = "fixed";
export const AREA_GROUP_MODE_AUTO_BY_SPOTS = "autoBySpots";
export const AREA_GROUP_MODE_USER_CREATED = "userCreated";
export type AreaGroupMode = "fixed" | "autoBySpots" | "userCreated";

export const AREA_SHOW_SOUND_INFO_NONE = "none";
export const AREA_SHOW_SOUND_INFO_MAIN = "main";
export const AREA_SHOW_SOUND_INFO_MAIN_AND_AMBIENT = "mainAndAmbient";
export type AreaShowSoundInfoMode = "none" | "main" | "mainAndAmbient";

export const AREA_SPOT_TRIGGER_MODE_AUTOMATIC = "automatic";
export const AREA_SPOT_TRIGGER_MODE_ON_DEMAND = "onDemand";
export type AreaSpotTriggerMode = "automatic" | "onDemand";

export const AREA_DISCOVER_MODE_PLAYING = "playing";
export const AREA_DISCOVER_MODE_TRIGGERABLE = "triggerable";
export const AREA_DISCOVER_MODE_ALL = "all";
export type AreaDiscoverMode = "playing" | "triggerable" | "all";

/**
 * Resolve effective autoPlay for an area. `autoPlay` takes priority; if
 * absent, fall back to the legacy `spotTriggerMode`. Default: true.
 * Mirrors the app-side rule in docs/SPACE_AUTOPLAY_AND_DISCOVER_MODE.md.
 */
export const resolveAreaAutoPlay = (area: Pick<Area, "autoPlay" | "spotTriggerMode"> | undefined): boolean => {
    if (area?.autoPlay === true || area?.autoPlay === false) {
        return area.autoPlay;
    }
    if (area?.spotTriggerMode === AREA_SPOT_TRIGGER_MODE_ON_DEMAND) {
        return false;
    }
    return true;
};

/**
 * Resolve effective discoverMode. Explicit value wins; otherwise derived
 * from autoPlay (true → playing, false → triggerable).
 */
export const resolveAreaDiscoverMode = (
    area: Pick<Area, "autoPlay" | "spotTriggerMode" | "discoverMode"> | undefined
): AreaDiscoverMode => {
    if (
        area?.discoverMode === AREA_DISCOVER_MODE_PLAYING
        || area?.discoverMode === AREA_DISCOVER_MODE_TRIGGERABLE
        || area?.discoverMode === AREA_DISCOVER_MODE_ALL
    ) {
        return area.discoverMode;
    }
    return resolveAreaAutoPlay(area) ? AREA_DISCOVER_MODE_PLAYING : AREA_DISCOVER_MODE_TRIGGERABLE;
};

export const AUDIO_MODE_DOWNLOAD = "download";
export const AUDIO_MODE_STREAM = "stream";
export type AudioMode = "download" | "stream";

export const AREA_FLOW_SPATIAL = "spatial";
export const AREA_FLOW_SEQUENTIAL = "sequential";
export type AreaFlowMode = "spatial" | "sequential";

export const AREA_STATUS_LIVE = "Live";
export const AREA_STATUS_ARCHIVED = "Archived";
export const AREA_STATUS_READONLY = "Readonly";

export type AreaSharedTriggerConfig = {
    type: "beacon" | "wifi" | "none";
    beacon?: {
        macAddr: string;
        uuid?: string;
    };
    wifi?: {
        name: string;
    };
}

export type AreaDefaults = {
    beaconTriggers?: BeaconTrigger[];
    wiFiTriggers?: WiFiApTrigger[];
    // Legacy single-trigger shape (kept as read fallback during migration).
    sharedTrigger?: AreaSharedTriggerConfig;
}

export type AreaLanguageOption = {
    code: string
    label: string
    flagUrl?: string
}

export const AREA_LANGUAGE_DOWNLOAD_ALL = "all";
export const AREA_LANGUAGE_DOWNLOAD_DEFAULT_ONLY = "defaultOnly";
export const AREA_LANGUAGE_DOWNLOAD_USER_CHOOSE = "userChoose";
export type AreaLanguageDownloadMode = "all" | "defaultOnly" | "userChoose";

export type AreaLanguages = {
    default: string
    available: AreaLanguageOption[]
    downloadMode?: AreaLanguageDownloadMode
}


export type SoundFile = {
    originalFileName?: string,
    fileName?: string,
    filePath?: string,
    ambient?: boolean,
    playOnes?: boolean,
    keepPlayingAfterMainFile?: boolean,
    delay?: number,
    fadeIn?: number,
    fadeOut?: number
    synchronize: boolean
    noSyncTime?: number
    language?: string
    volumeDb?: number
}

export type SoundFileLibraryEntry = {
    id?: string
    areaId: string
    objectId: string
    originalFileName?: string
    filePath?: string
    uploadedBy?: string
    mimeType?: string
    sizeBytes?: number
    durationMs?: number
    lastUsedPublishedAt?: number
    lastUsedPublishedVersionName?: string
    purgedAt?: number
    purgedBy?: string
    createdAt?: number
    updatedAt?: number
}

export type TextTranslation = {
    id: string
    areaId: string
    clientId: string
    languageCode: string
    translationKey: string
    text: string
    modifiedAt: number
}

export type AudioStorageBucket = {
    totalFilesCount: number
    totalFilesStorageSize: number
}

export type OrganizationPlan = {
    id: string
    name: string
    maxAdministrators: number
    maxEditors: number
    isDefault: boolean
    assignedOrganizationsCount: number
}

export type OrganizationRoleUsage = {
    administrators: number
    editors: number
}

export type AreaStatistics = {
    id?: string
    areaId: string
    audioStorage: {
        draft: AudioStorageBucket
        published: AudioStorageBucket
        total: AudioStorageBucket
    }
    updatedAt?: number
    createdAt?: number
}

export type ZoneSoundFiles = {
    ambient?: SoundFile,
    entry?: SoundFile,
    exit?: SoundFile,
    soundTest?: SoundFile,
}

type TriggerType = "Beacon" | "iBeacon" | "WiFiAP" | "GPS";

export interface Trigger {
    id: string
    type: TriggerType
    zoneId?: string
}

export interface BeaconTrigger extends Trigger {
    macAddr: string
    majorId: string
    minorId: string
    uuid?: string
    note?: string
}

export interface WiFiApTrigger extends Trigger {
    name: string
}

export interface GpsTrigger extends Trigger {
    shapeType: GpsShapeType
    polygonCoords?: LatLong[]
    circlePosition?: LatLong
    circleRadius?: number
}

export interface TriggerRules {
    rules: TriggerRule[],
    logic: RulesLogic
}

export interface TriggerRule {
    id: string
    type: RuleType
    eventType: RuleEventType
    spotId?: string
    spotKey?: string
    zoneKey?: string
    minMsSinceFinished?: number
    minMsSinceTriggered?: number
}

export type RuleType = "Spot" | "Ambient" | "DateTime" | "Time"

export type RuleEventType =
    | "SPOT_TRIGGERED" | "SPOT_NOT_TRIGGERED" | "SPOT_FINISHED" | "SPOT_NOT_FINISHED" | "SPOT_FINISHED_FOR_MS"
    | "AMBIENT_TRIGGERED" | "AMBIENT_NOT_TRIGGERED" | "AMBIENT_TIME_SINCE_TRIGGERED"

export enum RulesLogic {
    AND = "And",
    OR = "Or"
}

export enum GpsShapeType {
    Marker = "Marker",
    Circle = "Circle",
    Polygon = "Polygon"
}

// What to do with the sound when exiting the spot
export type ZonePlayMode = "stop" | "play";

export type SoundAreaName = "ambient" | "entry" | "exit" | "soundTest";


export interface Zone {
    id: string,
    name: string,
    label?: string,
    beaconSensitivity?: number,
    stopOtherAmbients?: boolean,
    soundFiles?: ZoneSoundFiles,
    beaconTriggers?: BeaconTrigger[],
    wiFiTriggers?: WiFiApTrigger[],
    gpsTriggers?: GpsTrigger[],
    triggerRules?: TriggerRules,
    playMode?: ZonePlayMode,
    playFullAfter?: number,
    loop: boolean,
    runOnes: boolean
}

export interface BoundariesZone extends Zone {
    triggerToActivate: boolean
}

export interface ExitZone extends Zone {
    markCompleted: boolean
}

export interface MaintenanceZone extends Zone {
    resetApps: boolean,
    coolDownPeriod?: number,
}

export type SpotPlayMode = "stop" | "pause" | "play";

export interface TriggerInfo {
    beaconTriggers?: BeaconTrigger[],
}

export interface SpotInfo {
    id: string,
    type: SpotType,
    name: string,
    modifiedAt: number,
    key: string
}

export interface Spot extends SpotInfo, TriggerInfo {
    area?: string,
    summary?: string,
    description?: string,
    coverImageUrl?: string,
    note?: string,
    priority: number;
    beaconSensitivity: number;
    soundFiles?: SoundFile[],
    wiFiTriggers?: WiFiApTrigger[],
    gpsTriggers?: GpsTrigger[],
    triggerRules?: TriggerRules,
    externalIdentifier?: string,
    pauseOtherAmbients?: boolean,
	duckOtherAmbients?: boolean,
	duckOtherAmbientsDb?: number,
	followMainSound?: boolean,
	playMode?: SpotPlayMode,
	playFullAfter?: number,
	loop?: boolean,
	runOnes?: boolean
	canBeStopped?: boolean // Can this spot be stopped by others with same priority?
	synchronizeEntry?: boolean // Can this spot be started if its group peers see it?
}

export interface MarkerSpot extends Spot {
    position: LatLong
}

export interface CircleSpot extends Spot {
    center: LatLong,
    radius: number
}

export interface PolygonSpot extends Spot {
    coords: LatLong[]
}

export type LatLong = {
    lat: number,
    lng: number
}

export type WalkLinePoint = {
    spotKey?: string
    lat?: number
    lng?: number
}

export type WalkLine = {
    id: string
    points: WalkLinePoint[]
}

export type WalkLines = {
    areaId: string
    lines: WalkLine[]
    modifiedAt?: number
}

export type Version = {
    id: string,
    areaId: string,
    modifiedAt: string;
    publishedAt: number,
    publishDate?: string,
    releaseNote: string,
    name: string,
    isActive: boolean
}
